import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, FolderKanban } from 'lucide-react';
import { format } from 'date-fns';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { buildTeamApiService } from '../../services/buildTeamApi';
import {
  BuildProject,
  BuildProjectStatus,
  BuildTag,
  ProjectCategory,
  BUILD_PROJECT_STATUSES,
  PROJECT_CATEGORIES,
} from '../../types/buildTeam';
import { cn } from '../../utils/cn';
import { ApogeePackSearchField } from '../apogee/ApogeePackSearchField';

export interface EditProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  project: BuildProject;
}

function formatStatusLabel(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function dueToDatetimeLocal(ts: number | null): string {
  if (ts == null) return '';
  const date = ts > 1e12 ? new Date(ts) : new Date(ts * 1000);
  return format(date, "yyyy-MM-dd'T'HH:mm");
}

const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

export function EditProjectModal({
  isOpen,
  onClose,
  onSuccess,
  project,
}: EditProjectModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [title, setTitle] = useState(project.title);
  const [description, setDescription] = useState(project.description);
  const [status, setStatus] = useState<BuildProjectStatus>(project.status);
  const [priority, setPriority] = useState(project.priority);
  const [category, setCategory] = useState<ProjectCategory | ''>(project.category ?? '');
  const [dueDateLocal, setDueDateLocal] = useState(dueToDatetimeLocal(project.due_date));
  const [modpackApogeeId, setModpackApogeeId] = useState<number | null>(project.apogee_id ?? null);
  const [tags, setTags] = useState<BuildTag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [initialTagIds, setInitialTagIds] = useState<string[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setTitle(project.title);
    setDescription(project.description);
    setStatus(project.status);
    setPriority(project.priority);
    setCategory(project.category ?? '');
    setDueDateLocal(dueToDatetimeLocal(project.due_date));
    setModpackApogeeId(project.apogee_id ?? null);
    setError(null);

    setIsLoadingTags(true);
    Promise.all([buildTeamApiService.listTags(), buildTeamApiService.getProjectTags(project.id)])
      .then(([allTags, projectTags]) => {
        setTags(allTags);
        const ids = projectTags.map((t) => t.id);
        setSelectedTagIds(ids);
        setInitialTagIds(ids);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load tags');
      })
      .finally(() => setIsLoadingTags(false));
  }, [isOpen, project]);

  const handleToggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!description.trim()) {
      setError('Description is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await buildTeamApiService.updateProject(project.id, {
        title: title.trim(),
        description: description.trim(),
        status,
        priority,
        category: category || null,
        apogee_id: modpackApogeeId,
        due_date: dueDateLocal.trim()
          ? Math.floor(new Date(dueDateLocal).getTime() / 1000)
          : null,
      });

      const toAdd = selectedTagIds.filter((id) => !initialTagIds.includes(id));
      const toRemove = initialTagIds.filter((id) => !selectedTagIds.includes(id));
      await Promise.all([
        ...toAdd.map((id) => buildTeamApiService.tagProject(project.id, id)),
        ...toRemove.map((id) => buildTeamApiService.untagProject(project.id, id)),
      ]);

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update project');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-project-title"
      >
        <div className="modal-header">
          <h2 id="edit-project-title" className="modal-title flex items-center gap-2">
            <FolderKanban className="w-5 h-5 text-accent-300" />
            Edit project
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="modal-close touch-manipulation"
            aria-label="Close"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="alert alert-danger mb-4">
            <AlertCircle />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={LABEL_CLASS}>Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="glass-input w-full"
              required
            />
          </div>

          <div>
            <label className={LABEL_CLASS}>Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="glass-input w-full"
              rows={4}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as BuildProjectStatus)}
                className="glass-select w-full"
              >
                {BUILD_PROJECT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {formatStatusLabel(s)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={LABEL_CLASS}>Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as ProjectCategory | '')}
                className="glass-select w-full"
              >
                <option value="">None</option>
                {PROJECT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {formatStatusLabel(c)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={LABEL_CLASS}>Priority (1–10)</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={1}
                max={10}
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                className="flex-1 h-2 rounded-full appearance-none bg-glass-200/30 accent-accent-500"
              />
              <input
                type="number"
                min={1}
                max={10}
                value={priority}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (v >= 1 && v <= 10) setPriority(v);
                }}
                className="glass-input w-16 text-center tabular-nums"
              />
            </div>
          </div>

          <div>
            <label className={LABEL_CLASS}>Due date</label>
            <input
              type="datetime-local"
              value={dueDateLocal}
              onChange={(e) => setDueDateLocal(e.target.value)}
              className="glass-input w-full"
            />
          </div>

          <ApogeePackSearchField
            value={modpackApogeeId}
            onChange={setModpackApogeeId}
            label="Linked modpack (optional)"
            compact
            description="Stored as project apogee_id. Clear the selection to remove the link."
            disabled={isSubmitting}
          />

          <div>
            <label className={LABEL_CLASS}>Tags</label>
            {isLoadingTags ? (
              <div className="p-3 flex items-center justify-center">
                <Loader className="w-4 h-4 animate-spin text-accent-400" />
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleToggleTag(tag.id)}
                    className={cn(
                      'chip transition-colors',
                      selectedTagIds.includes(tag.id)
                        ? 'text-white'
                        : 'hover:text-white hover:border-glass-200/25'
                    )}
                    aria-pressed={selectedTagIds.includes(tag.id)}
                    style={
                      selectedTagIds.includes(tag.id)
                        ? { borderColor: tag.color, backgroundColor: `${tag.color}20` }
                        : undefined
                    }
                  >
                    {tag.name}
                  </button>
                ))}
                {tags.length === 0 && (
                  <span className="text-xs text-glass-400">No tags available</span>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="glass-button-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : null}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
